import { createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { WrappedImage } from '../../components/image-component/wrapped-image';
import { Text } from '../../ui-components/text/text';
import { ClientsProps, ClientType } from './clients.types';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { breakpoints } from '../../style/theme';
import clsx from 'clsx';

export const ClientSelector = (props: ClientsProps) => {
    const { clients = [], showTitles, animateOpacity, controlLayout } = props;
    const { viewport } = useAppContext();
    const isDesktop = () => viewport.width >= breakpoints.desktop && viewport.width < breakpoints.desktopXL;
    const getHref = (clientId: number) => {
        const client = clients.find((client) => client.id === clientId);
        if (!client) {
            return '';
        }

        if (client.url && client.url.url) {
            return client.url.url;
        }

        if (client.hasPostContent && client.permalink) {
            return client.permalink;
        }

        return client.client_link;
    };

    const getAngle = () => {
        const randomValue = Math.random();
        let angle = 0;

        if (randomValue < 0.33) {
            angle = Math.random() * -5 - 2;
        } else if (randomValue > 0.66) {
            angle = Math.random() * 5 + 2;
        }

        return angle;
    };

    const opacityLevels = clients.map(() => createSignal(Math.random() * (1 - 0.6) + 0.6));
    const isHovered = clients.map(() => createSignal(false));
    const intervals: NodeJS.Timeout[] = [];

    onMount(() => {
        opacityLevels.forEach(([opacity, setOpacity], index) => { //eslint-disable-line
            const [hovered] = isHovered[index];

            const animateOpacity = () => {
                if (hovered()) return;

                setOpacity(1); 
                setTimeout(() => {
                    if (hovered()) return;
                    setOpacity(Math.random() * 0.3 + 0.4);
                }, Math.random() * 3000 + 2000);
            };

            const interval = setInterval(animateOpacity, Math.random() * 4000 + 6000);
            intervals.push(interval);

            setTimeout(animateOpacity, Math.random() * 3500 + 2000);
        });
    });

    onCleanup(() => {
        intervals.forEach(clearInterval);
    });

    const getMarginClasses = (index: number) => {
        const forAll = clsx({
            'aspect-square': !showTitles,
            'tablet:-mt-12 tablet:mb-12': (index % 3) === 1 && animateOpacity && !isDesktop(),
            'bigTablet:-mt-12 bigTablet:mb-12': (index % 3) === 1 && animateOpacity && !isDesktop(),
        });

        if (!controlLayout.isControlled) {
            return forAll + ' ' + clsx({
                'laptop:-mt-12 laptop:mb-12': (index % 3) === 1 && animateOpacity && !isDesktop(),
                'desktop:-mt-12 desktop:mb-12': ((index % 4) === 1 || (index % 4) === 3) && animateOpacity && isDesktop(), 
                'desktopXL:-mt-12 desktopXL:mb-12': ((index % 5) === 1 || (index % 5) === 3) && animateOpacity && !isDesktop(),
                'desktop:-mt-0 desktop:mb-0': ((index % 4) === 0 || index % 4 === 2) && animateOpacity && isDesktop(), 
                'desktopXL:mt-0 desktopXL:mb-0': ((index % 5) === 0 || index % 5 === 2 || index % 5 === 4) && animateOpacity && !isDesktop(), 
            });
        }

        if (controlLayout.isControlled && controlLayout.rowLength === 3) {
            return forAll + ' ' + clsx({
                'laptop:-mt-12 laptop:mb-12': (index % 3) === 1 && animateOpacity,
            });
        }

        if (controlLayout.isControlled && controlLayout.rowLength === 4) {
            return forAll + ' ' + clsx({
                'desktop:-mt-12 desktop:mb-12': ((index % 4) === 1 || (index % 4) === 3) && animateOpacity,
                'desktop:-mt-0 desktop:mb-0': ((index % 4) === 0 || index % 4 === 2) && animateOpacity,
            });
        }
    };

    const normalizedClients = createMemo(() =>
        clients.map(client => ({
            ...client,
            priority: typeof client.priority === 'number' ? client.priority : 0,
            /* eslint-disable camelcase */
            client_priority:
                typeof client.client_priority === 'string'
                    ? parseInt(client.client_priority, 10) || 0
                    : (typeof client.client_priority === 'number' ? client.client_priority : 0),
            /* eslint-enable camelcase */
        }))
    );

    const sortedClients = createMemo(() =>
        [...normalizedClients()].sort((a, b) => {
            if (a.priority !== b.priority) return b.priority - a.priority;
            return b.client_priority - a.client_priority;
        })
    );
      
    return (
        <div class='container mx-auto'>
            <Show when={sortedClients().length > 0} fallback={<div />}>
                <ul
                    class='tablet:grid-cols-3 bigTablet:grid-cols-3 laptop:grid-cols-3 grid grid-cols-2 gap-6 tablet:gap-12'
                    classList={{
                        'tablet:gap-x-12 gap-y-4 tablet:gap-y-2': showTitles,
                        'desktop:grid-cols-4 desktopXL:grid-cols-5': !controlLayout.isControlled,
                        'desktop:grid-cols-4': controlLayout.isControlled && controlLayout.rowLength === 4,
                    }}
                >
                    <For each={sortedClients()}>
                        {(client: ClientType, index) => {
                            const angle = getAngle();
                            const [opacity] = opacityLevels[index()];
                            const [hovered, setHovered] = isHovered[index()];

                            return (
                                <li class={'h-full w-full tablet:pb-4 desktop:pb-6 ' + getMarginClasses(index())}>
                                    <a
                                        href={getHref(client.id)}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        class='group block transition-all duration-400 ease-in-out hover:animate-none focus:animate-none hover:opacity-100 focus-visible:opacity-100'
                                        aria-label={client.client_name}
                                        style={{ opacity: hovered() ? 1 : opacity() }}
                                        onMouseEnter={() => setHovered(true)}
                                        onMouseLeave={() => setHovered(false)}
                                    >
                                        <Show when={client.featured_image}>
                                            <div
                                                class='rounded-m mb-2 tablet:mb-s aspect-square group-hover:blur-none group-focus:blur-none bg-white flex items-center justify-center p-m transition-all duration-400 ease-in-out hover:scale-110 focus:scale-110'
                                                style={{
                                                    rotate: animateOpacity && !hovered() ? `${angle}deg`: '',
                                                }}
                                            >
                                                <WrappedImage
                                                    width={300}
                                                    renderWidth={'100%'}
                                                    renderHeight={'100%'}
                                                    height={'auto'}
                                                    aspectRatio={'clients'}
                                                    image={client.featured_image}
                                                    classes='object-contain w-full h-full '
                                                    roundCorners='none'
                                                />
                                            </div>
                                        </Show>
                                        <Show when={showTitles}>
                                            <Text classes='text-center text-paragraphS' disableMargins>{client.client_name}</Text>
                                        </Show>
                                    </a>
                                </li>
                            );
                        }}
                    </For>
                </ul>
            </Show>
        </div>
    );
};

ClientSelector.parseProps = (atts: ClientsProps) => {
    return {
        clients: atts.clients ?? [],
        showTitles: atts.showTitles ?? true,
        animateOpacity: atts.animateOpacity ?? true,
        controlLayout: atts.controlLayout ?? {
            isControlled: false,
            rowLength: 3
        }
    };
};
